// useV2AuthForm.js
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  loginUser,
  registerUser,
  setLocalStorageAuth,
  openVerificationPage,
} from '../services/v2AuthService';

/**
 * Manages login/signup form state and async handlers.
 *
 * @param {Object} options
 * @param {Function} options.onLoginSuccess - Called with no args after successful login;
 *                                            parent navigates to home
 * @returns {Object} form state + handlers
 */
function useV2AuthForm({ onLoginSuccess }) {
  // ── Field state ──────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [phone, setPhone] = useState('');
  const [dialCode, setDialCode] = useState('1');
  const [selectedCountry, setSelectedCountry] = useState(null); // {name, code, dial_code}
  const [showPassword, setShowPassword] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  // ── Feedback state ───────────────────────────────────────────────────────
  const [error, setErrorRaw] = useState('');
  const [success, setSuccessRaw] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── Timers for auto-clear ────────────────────────────────────────────────
  const errorTimerRef = useRef(null);
  const successTimerRef = useRef(null);

  const setError = useCallback((msg, durationMs = 3000) => {
    setErrorRaw(msg);
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    if (msg) {
      errorTimerRef.current = setTimeout(() => setErrorRaw(''), durationMs);
    }
  }, []);

  const setSuccess = useCallback((msg) => {
    setSuccessRaw(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    if (msg) {
      successTimerRef.current = setTimeout(() => setSuccessRaw(''), 3000);
    }
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  // ── Login handler ────────────────────────────────────────────────────────

  /**
   * Submits email/password login. Calls loginUser service.
   * On success: persists auth keys and calls onLoginSuccess.
   * On unverified: opens verification page in new tab.
   * On failure: sets error message.
   */
  const handleLogin = useCallback(async () => {
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      console.log('[useV2AuthForm.js] r1: handleLogin called');
      const data = await loginUser(email, password);

      if (data.message === 'User Verification Required') {
        // Unverified user
        openVerificationPage(email);
        setError('Your email is not verified. A verification link has been sent.', 6000);
        setLoading(false);
        return;
      }
      if (data.data === false) {
        console.log('[useV2AuthForm.js] r2: login failed data message=', data.message);
        if (data.message === 'Password does not match') {
          setError('Password Incorrect');
        }
        else if (data.message === 'User does not exist') {
          setError('Account Not found');
        }
        else{
          setError('Invalid credentials. Please try again.');
        }
        // setError('Invalid credentials. Please try again.');
        setLoading(false);
        return;
      }
      setLocalStorageAuth(data);
      console.log('[useV2AuthForm.js] r2: login success, navigating to home');
      onLoginSuccess();
    } catch (err) {
      console.log('[useV2AuthForm.js] r3: login error', err?.response?.status);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, onLoginSuccess, setError]);

  // ── Signup handler ───────────────────────────────────────────────────────

  /**
   * Validates phone and submits registration. Calls registerUser service.
   * On success: sets success message and resets to login mode via onSignupSuccess callback.
   *
   * @param {Function} onSignupSuccess - Called after successful registration; parent sets isSignUp=false
   */
  const handleSignup = useCallback(async (onSignupSuccess) => {
    if (!fullName || !email || !orgName || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    // Phone validation (optional field)
    if (phone) {
      if (!/^\d+$/.test(phone)) {
        setPhoneError('Phone number must contain only digits.');
        return;
      }
      if (!isPhoneValid) {
        setPhoneError('Please enter a valid phone number for the selected country.');
        return;
      }
    }

    setPhoneError('');
    setLoading(true);
    setError('');
    try {
      console.log('[useV2AuthForm.js] r4: handleSignup called for email domain=', email?.split('@')[1]);
      const data = await registerUser({
        email,
        orgName,
        password,
        userName: fullName,
        dialCode,
        phone,
        country: selectedCountry?.name ?? '',
      });

      if (data.message_type === 'Success') {
        const verificationStatus = data.verification_status || '';
        if (verificationStatus.includes('Email Confirmation Link Sent')) {
          setSuccess('Registration successful. Please verify your email before logging in.');
        } else {
          setSuccess('Registration successful! You can now log in.');
        }
        onSignupSuccess();
      } else {
        setError(data.message_type || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.log('[useV2AuthForm.js] r5: signup error', err?.response?.status);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fullName, email, orgName, password, phone, isPhoneValid, dialCode, selectedCountry, setError, setSuccess]);

  // ── Field change handlers ────────────────────────────────────────────────

  const clearForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setFullName('');
    setOrgName('');
    setPhone('');
    setDialCode('1');
    setSelectedCountry(null);
    setShowPassword(false);
    setIsPhoneValid(false);
    setErrorRaw('');
    setSuccessRaw('');
    setPhoneError('');
  }, []);

  return {
    // Field state
    email, setEmail,
    password, setPassword,
    fullName, setFullName,
    orgName, setOrgName,
    phone, setPhone,
    dialCode, setDialCode,
    selectedCountry, setSelectedCountry,
    showPassword, setShowPassword,
    isPhoneValid, setIsPhoneValid,
    // Feedback state
    error, success, phoneError,
    setError, setSuccess, setPhoneError,
    loading,
    // Action handlers
    handleLogin,
    handleSignup,
    clearForm,
  };
}

export default useV2AuthForm;
